"use client";

import React, { useState, useEffect } from "react";
import { Plus, Trash2, GripVertical } from "lucide-react";
import Image from "next/image";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  PhotoshopLogo,
  IllustratorLogo,
  WordLogo,
  ExcelLogo,
  PowerPointLogo,
} from "@/components/assets/logos";
import { AppFile } from "@/types";
import { useMedia } from "@/contexts/media-context";
import Media from "./media-list";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@/components/ui/drawer";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

interface MediaDialogProps {
  selectedFiles: AppFile[];
  onFileSelect: (files: AppFile[]) => void;
  initialData?: { images?: string[] };
  maxFiles?: number;
  folder?: string;
  trigger?: React.ReactNode;
  onImageUpload?: (
    imageData: string,
    fileName?: string,
    subFolder?: string
  ) => Promise<string>;
  fileTypes?: string[];
}

interface MediaContentProps {
  files: AppFile[];
  setFiles: React.Dispatch<React.SetStateAction<AppFile[]>>;
  dialogSelectedFiles: string[];
  setDialogSelectedFiles: (files: string[]) => void;
  selectedFiles: AppFile[];
  maxFiles?: number;
  isSaving: boolean;
  handleSave: () => Promise<void>;
  handleDelete: (publicIds: string[]) => Promise<void>;
  handleSetSelected: () => void;
  onClose: () => void;
  isLoading: boolean;
  drawerHidden: (hidden: boolean) => void;
  onRefresh: () => Promise<void>;
  onImageUpload?: (
    imageData: string,
    fileName?: string,
    subFolder?: string
  ) => Promise<string>;
  fileTypes?: string[];
}

const MediaContent = ({
  files,
  setFiles,
  dialogSelectedFiles,
  setDialogSelectedFiles,
  selectedFiles,
  maxFiles = Infinity,
  isSaving,
  handleSave,
  handleDelete,
  handleSetSelected,
  onClose,
  isLoading,
  drawerHidden,
  onRefresh,
  onImageUpload,
  fileTypes,
}: MediaContentProps) => {
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const HeaderComponent = isMobile ? DrawerHeader : DialogHeader;
  const TitleComponent = isMobile ? DrawerTitle : DialogTitle;
  const FooterComponent = isMobile ? DrawerFooter : DialogFooter;

  return (
    <>
      <HeaderComponent>
        <TitleComponent>
          Select Media{" "}
          {maxFiles === 1 ? "(Single File)" : `(Max ${maxFiles} Files)`}
        </TitleComponent>
        {!isMobile && (
          <DialogDescription>
            Choose files from your media library or upload new ones.
          </DialogDescription>
        )}
      </HeaderComponent>

      <div className='flex-1 overflow-y-auto min-h-0 max-h-[60vh]'>
        <div className='p-4'>
          <Media
            files={files}
            setFiles={(value) =>
              setFiles(typeof value === "function" ? value(files) : value)
            }
            selectedFiles={dialogSelectedFiles}
            setSelectedFiles={(newSelected) => {
              if (newSelected.length + selectedFiles.length > maxFiles) {
                toast({
                  variant: "destructive",
                  title: "Error",
                  description: `You can only select up to ${maxFiles} file${
                    maxFiles === 1 ? "" : "s"
                  }`,
                });
                return;
              }
              setDialogSelectedFiles(newSelected);
            }}
            onSave={handleSave}
            isSaving={isSaving}
            onDelete={handleDelete}
            isLoading={isLoading}
            disabledFiles={selectedFiles.map((file) => file.id)}
            onDrawerStateChange={drawerHidden}
            onRefresh={onRefresh}
            onImageUpload={onImageUpload}
            fileTypes={fileTypes}
          />
        </div>
      </div>

      <FooterComponent className='mt-auto pt-4 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60'>
        <div className='flex justify-between items-center w-full'>
          <div className='text-sm text-muted-foreground'>
            {dialogSelectedFiles.length} items selected
          </div>
          <div className='flex gap-2'>
            <Button variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSetSelected}
              disabled={dialogSelectedFiles.length === 0}
            >
              Set Selected
            </Button>
          </div>
        </div>
      </FooterComponent>
    </>
  );
};

const DesktopDialog = ({
  open,
  onOpenChange,
  trigger,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: React.ReactNode;
  children: React.ReactNode;
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className='max-w-4xl max-h-[90vh] overflow-hidden flex flex-col z-[202]'>
        {children}
      </DialogContent>
    </Dialog>
  );
};

const MobileDrawer = ({
  open,
  onOpenChange,
  trigger,
  children,
  drawerHidden,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: React.ReactNode;
  children: React.ReactNode;
  drawerHidden: boolean;
}) => {
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerTrigger asChild onClick={() => onOpenChange(true)}>
        {trigger}
      </DrawerTrigger>
      <DrawerContent
        className={cn(
          "z-[101]",
          drawerHidden && "translate-y-full transition-transform"
        )}
      >
        {children}
      </DrawerContent>
    </Drawer>
  );
};

export default function MediaDialog({
  selectedFiles,
  onFileSelect,
  initialData,
  maxFiles = Infinity,
  folder = "default",
  trigger,
  onImageUpload,
  fileTypes,
}: MediaDialogProps) {
  const {
    files,
    setFiles,
    handleSave,
    handleDelete,
    handleRefresh,
    isLoading,
    isSaving,
  } = useMedia();
  const [open, setOpen] = useState(false);
  const [dialogSelectedFiles, setDialogSelectedFiles] = useState<string[]>([]);
  const [drawerHidden, setDrawerHidden] = useState(false);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (
      selectedFiles.length === 0 &&
      files.length === 0 &&
      initialData?.images?.length
    ) {
      const initialFiles: AppFile[] = initialData.images.map((url, index) => ({
        id: `existing-${index}`,
        name: url.split("/").pop() || `image-${index}`,
        url: url,
        type: url.split(".").pop()?.toLowerCase() || "jpg",
        size: "0",
        dateAdded: new Date(),
      }));
      onFileSelect(initialFiles);
      setFiles(initialFiles);
    }
  }, [files.length, initialData, onFileSelect, setFiles, selectedFiles.length]);

  const handleRemoveFile = (fileId: string) => {
    const updatedFiles = selectedFiles.filter((file) => file.id !== fileId);
    onFileSelect(updatedFiles);
  };

  const handleSetSelected = () => {
    const selectedFileObjects = files.filter((file) =>
      dialogSelectedFiles.includes(file.id)
    );

    const totalFiles = selectedFiles.length + selectedFileObjects.length;
    if (totalFiles > maxFiles) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `You can only select up to ${maxFiles} file${
          maxFiles === 1 ? "" : "s"
        }`,
      });
      return;
    }

    onFileSelect([...selectedFiles, ...selectedFileObjects]);
    setDialogSelectedFiles([]);
    setOpen(false);
  };

  const handleSaveWithFolder = async () => {
    try {
      await handleSave();
    } catch (error) {
      console.error("Error saving files:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save files. Please try again.",
      });
    }
  };

  const reorderFiles = (startIndex: number, endIndex: number) => {
    const result = Array.from(selectedFiles);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    onFileSelect(result);
  };

  const FilePreview = ({ file, index }: { file: AppFile; index: number }) => {
    const getFilePreview = () => {
      switch (file.type.toLowerCase()) {
        case "psd":
          return (
            <PhotoshopLogo
              className='h-full w-full object-contain p-2'
              alt={file.name}
            />
          );
        case "ai":
          return (
            <IllustratorLogo
              className='h-full w-full object-contain p-2'
              alt={file.name}
            />
          );
        case "doc":
        case "docx":
          return (
            <WordLogo
              className='h-full w-full object-contain p-2'
              alt={file.name}
            />
          );
        case "xls":
        case "xlsx":
          return (
            <ExcelLogo
              className='h-full w-full object-contain p-2'
              alt={file.name}
            />
          );
        case "ppt":
        case "pptx":
          return (
            <PowerPointLogo
              className='h-full w-full object-contain p-2'
              alt={file.name}
            />
          );
        default:
          return (
            <Image
              src={file.url}
              alt={file.name}
              className='h-full w-full rounded-md object-cover bg-white'
              width={0}
              height={0}
              sizes='100vw'
              style={{ width: "100%", height: "100%" }}
            />
          );
      }
    };

    return (
      <Card
        key={file.id}
        className='p-3 cursor-move flex items-center justify-between gap-2'
        draggable
        onDragStart={(e) =>
          e.dataTransfer.setData("text/plain", index.toString())
        }
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const startIndex = parseInt(e.dataTransfer.getData("text/plain"));
          reorderFiles(startIndex, index);
        }}
      >
        <div className='flex items-center gap-2 flex-1 min-w-0'>
          <GripVertical className='h-5 w-5 text-gray-400 shrink-0' />
          <div className='h-16 w-16 flex-shrink-0'>{getFilePreview()}</div>
          <div className='flex-1 min-w-0 max-w-[200px]'>
            <p className='font-medium truncate' title={file.name}>
              {file.name}
            </p>
            <p className='text-sm text-gray-500'>.{file.type}</p>
          </div>
        </div>
        <Button
          variant='ghost'
          size='sm'
          onClick={() => handleRemoveFile(file.id)}
          className='text-red-500 hover:text-red-700 shrink-0 ml-2'
        >
          <Trash2 className='h-4 w-4' />
        </Button>
      </Card>
    );
  };

  const handleClose = () => {
    setDialogSelectedFiles([]);
    setOpen(false);
  };

  const defaultTrigger = (
    <Button
      variant='outline'
      size='icon'
      className='rounded-full'
      disabled={selectedFiles.length >= maxFiles}
    >
      <Plus className='h-4 w-4' />
    </Button>
  );

  const content = (
    <MediaContent
      {...{
        files,
        setFiles,
        dialogSelectedFiles,
        setDialogSelectedFiles,
        selectedFiles,
        maxFiles,
        isSaving,
        handleSave: handleSaveWithFolder,
        handleDelete,
        handleSetSelected,
        onClose: handleClose,
        isLoading,
        drawerHidden: setDrawerHidden,
        onRefresh: handleRefresh,
        folder,
        onImageUpload,
        fileTypes,
      }}
    />
  );

  const handleOpen = (value: boolean) => {
    setOpen(value);
    if (!value) {
      handleClose();
    }
  };

  return (
    <div className='space-y-4'>
      {isMobile ? (
        <MobileDrawer
          open={open}
          onOpenChange={handleOpen}
          trigger={trigger || defaultTrigger}
          drawerHidden={drawerHidden}
        >
          {content}
        </MobileDrawer>
      ) : (
        <DesktopDialog
          open={open}
          onOpenChange={handleOpen}
          trigger={trigger || defaultTrigger}
        >
          {content}
        </DesktopDialog>
      )}

      {isLoading ? (
        <div className='flex items-center justify-center p-4'>
          <LoadingSpinner className='h-6 w-6' />
          <span className='ml-2'>Loading media...</span>
        </div>
      ) : (
        <div className='space-y-2'>
          {selectedFiles.map((file, index) => (
            <FilePreview key={file.id} file={file} index={index} />
          ))}
        </div>
      )}
    </div>
  );
}
